import React from "react";
import { Lock, LockOpen, ThumbsUp, ThumbsDown, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { ciApi } from "../ciApi";
import { Panel, Kicker, DataTable } from "../ciWidgets";

const LevelChip = ({ level }) => (
  <span
    className={`text-[10px] font-bold tracking-[0.1em] uppercase ${
      level === "high" ? "text-emerald-300" : level === "low" ? "text-white/40" : "text-amber-300"
    }`}
  >
    {level || "—"}
  </span>
);

const StatusChip = ({ status }) => {
  const s = status || "pending";
  const cls =
    s === "accepted"
      ? "text-emerald-300 border-emerald-400/40 bg-emerald-400/10"
      : s === "rejected"
      ? "text-red-300 border-red-400/40 bg-red-400/10"
      : "text-white/45 border-white/20";
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] font-bold tracking-[0.12em] uppercase ${cls}`}>
      {s}
    </span>
  );
};

export const RoadmapTab = ({ report, onReload }) => {
  const recs = report.recommendations || [];
  const roadmap = report.roadmap || [];
  const vt = report.value_tracking || [];
  const locked = !!report.locks?.recommendations?.locked;

  const setStatus = async (idx, status) => {
    if (locked) {
      toast.error("Recommendations are locked — unlock to change decisions");
      return;
    }
    try {
      await ciApi.patch(`/reports/${report.id}/recommendations/${idx}/status`, { status });
      toast.success(`Recommendation ${status === "pending" ? "reset" : status}`);
      onReload && (await onReload());
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    }
  };

  const toggleLock = async () => {
    try {
      await ciApi.patch(`/reports/${report.id}/locks`, { section: "recommendations", locked: !locked });
      toast.success(locked ? "Recommendations unlocked" : "Recommendations locked & approved");
      onReload && (await onReload());
    } catch {
      toast.error("Failed to update the lock");
    }
  };

  const accepted = recs.filter((r) => r.status === "accepted").length;
  const rejected = recs.filter((r) => r.status === "rejected").length;

  return (
    <div className="space-y-6" data-testid="ci-tab-roadmap">
      <Panel className="p-7">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-4">
            <Kicker>Strategic recommendations — ranked</Kicker>
            <span className="text-[11px] text-white/40">
              {accepted} accepted · {rejected} rejected · {recs.length - accepted - rejected} pending
            </span>
          </div>
          <button
            onClick={toggleLock}
            data-testid="ci-recs-lock-btn"
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11px] font-bold tracking-wide transition-colors ${
              locked
                ? "border-amber-400/50 text-amber-300 bg-amber-400/10 hover:bg-amber-400/20"
                : "border-white/20 text-white/55 hover:text-emerald-300 hover:border-emerald-400/50"
            }`}
          >
            {locked ? <Lock className="w-3.5 h-3.5" /> : <LockOpen className="w-3.5 h-3.5" />}
            {locked ? "Locked & approved" : "Lock decisions"}
          </button>
        </div>
        <DataTable
          testId="ci-recommendations-table"
          headers={["#", "Recommendation", "Business value", "Urgency", "Effort", "Investment", "Time to value", "Board decision"]}
          rows={recs.map((r, i) => [
            <span className="font-serif text-lg text-teal-300 tabular-nums">{i + 1}</span>,
            <div className={r.status === "rejected" ? "opacity-40" : ""}>
              <div className={`font-medium text-white ${r.status === "rejected" ? "line-through" : ""}`}>{r.title}</div>
              <div className="text-[12px] text-white/50 leading-relaxed mt-0.5">{r.rationale}</div>
            </div>,
            <LevelChip level={r.business_value} />,
            <LevelChip level={r.urgency} />,
            <LevelChip level={r.effort} />,
            r.investment,
            r.time_to_value,
            <div className="flex items-center gap-1.5">
              <StatusChip status={r.status} />
              {!locked && (
                <span className="inline-flex items-center gap-1 ml-1">
                  {(r.status || "pending") !== "accepted" && (
                    <button
                      onClick={() => setStatus(i, "accepted")}
                      data-testid={`ci-rec-accept-${i}`}
                      title="Accept"
                      className="p-1.5 rounded-full text-white/35 hover:text-emerald-300 hover:bg-emerald-400/10 transition-colors"
                    >
                      <ThumbsUp className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {(r.status || "pending") !== "rejected" && (
                    <button
                      onClick={() => setStatus(i, "rejected")}
                      data-testid={`ci-rec-reject-${i}`}
                      title="Reject"
                      className="p-1.5 rounded-full text-white/35 hover:text-red-300 hover:bg-red-400/10 transition-colors"
                    >
                      <ThumbsDown className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {(r.status || "pending") !== "pending" && (
                    <button
                      onClick={() => setStatus(i, "pending")}
                      data-testid={`ci-rec-reset-${i}`}
                      title="Reset to pending"
                      className="p-1.5 rounded-full text-white/35 hover:text-white/70 transition-colors"
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                    </button>
                  )}
                </span>
              )}
            </div>,
          ])}
        />
        <div className="text-[11px] text-white/35 mt-3">
          Rejected recommendations are excluded from the PPTX board deck and marked in the PDF board pack.
        </div>
      </Panel>

      <Panel className="p-7">
        <Kicker className="mb-6">36-month transformation roadmap</Kicker>
        <div className="relative pl-6 space-y-8 before:absolute before:left-1.5 before:top-2 before:bottom-2 before:w-px before:bg-white/15">
          {roadmap.map((phase, i) => (
            <div key={i} className="relative" data-testid={`ci-roadmap-phase-${i}`}>
              <span className="absolute -left-6 top-1 w-3 h-3 rounded-full bg-teal-400 border-4 border-[#0c1626]" />
              <div className="font-serif text-lg text-amber-300 mb-3">{phase.phase}</div>
              <div className="grid md:grid-cols-2 gap-3">
                {(phase.initiatives || []).map((init, j) => (
                  <div key={j} className="rounded-lg border border-white/10 bg-[#060d18] p-4">
                    <div className="text-[13px] font-semibold text-white mb-1.5 leading-snug">{init.action}</div>
                    <div className="text-[12px] text-white/50 leading-relaxed mb-2.5">{init.rationale}</div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
                      <div><span className="text-white/35">Owner:</span> <span className="text-white/65">{init.owner}</span></div>
                      <div><span className="text-white/35">KPI:</span> <span className="text-white/65">{init.kpi}</span></div>
                      <div><span className="text-white/35">Baseline → Target:</span> <span className="text-white/65">{init.baseline} → {init.target}</span>{init.date_flag && <span className="text-amber-300 text-[11px] ml-2">⚠ {init.date_flag}</span>}</div>
                      <div><span className="text-white/35">Milestone:</span> <span className="text-white/65">{init.milestone}</span></div>
                      <div><span className="text-white/35">Investment:</span> <span className="text-white/65">{init.investment}</span></div>
                      <div><span className="text-white/35">Benefit:</span> <span className="text-teal-200">{init.expected_benefit}</span></div>
                      {init.dependency && <div className="col-span-2"><span className="text-white/35">Dependency:</span> <span className="text-white/65">{init.dependency}</span></div>}
                      {init.risk && <div className="col-span-2"><span className="text-white/35">Risk:</span> <span className="text-amber-200/80">{init.risk}</span></div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Panel>

      <Panel className="p-7">
        <Kicker className="mb-4">Value-tracking framework · benefit register</Kicker>
        <DataTable
          testId="ci-value-tracking-table"
          headers={["Initiative", "Value category", "Baseline", "Target", "Expected value", "Value owner", "Finance validation", "Frequency", "Confidence"]}
          rows={vt.map((v) => [
            <span className="font-medium text-white">{v.initiative}</span>,
            v.value_category, v.baseline, v.target,
            <span className="text-amber-200">{v.expected_value}</span>,
            v.value_owner,
            <span className="text-[12px]">{v.finance_validation}</span>,
            v.frequency,
            <LevelChip level={v.confidence} />,
          ])}
        />
      </Panel>
    </div>
  );
};
